import * as Sentry from '@sentry/react';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { toast } from 'sonner';
import { AuthGuard } from '@/components/guards/AuthGuard';
import { LaunchGate } from '@/components/guards/LaunchGate';
import { RoleGuard } from '@/components/guards/RoleGuard';
import { SuperAdminGuard } from '@/components/guards/SuperAdminGuard';
import { QueryErrorBoundary } from '@/components/shared/QueryErrorBoundary';
import RouteLoadingFallback from '@/components/shared/RouteLoadingFallback';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { DemoModeProvider } from '@/contexts/DemoModeContext';
import { EntitlementsProvider } from '@/contexts/EntitlementsContext';
import { VenueProfileProvider } from '@/contexts/VenueProfileContext';
// Lazy imports — everything else
import {
  AccountLayout,
  AdminAuditLog,
  AdminDashboard,
  AdminEntitlements,
  AdminLayout,
  AdminTableBrowser,
  AnalyticsLayout,
  Calendar,
  ChangeEmail,
  CloseAccount,
  CommunicationPreferences,
  ContactInfo,
  Dashboard,
  DemoDataAdmin,
  DesignPartners,
  EventOverlayDashboard,
  Events,
  ForgotPassword,
  GettingStarted,
  GMReportPage,
  GoogleAdsDashboard,
  HiddenCostArticle,
  Hyped,
  Index,
  Insights,
  Invite,
  LinkedAccounts,
  MarketingAudiencesPage,
  MarketingDistribution,
  MarketingEmail,
  MarketingInstagramGrowth,
  MarketingMetaAds,
  MarketingOverview,
  MarketingSettingsPage,
  MarketingSmsPage,
  MetaAdsDashboard,
  NotFound,
  Onboarding,
  OurStory,
  Password,
  PaymentMethods,
  PersonalData,
  PitchDashboard,
  PitchGMReport,
  PitchHyped,
  PitchInsights,
  PitchIntegrations,
  PitchMarketing,
  PitchScreenshots,
  PitchStaffing,
  PitchVIPProfile,
  PrivacyPolicy,
  ReportsPage,
  ResetPassword,
  SecurityPolicy,
  Settings,
  SocialPerformanceDashboard,
  SubProcessors,
  Team,
  TermsOfService,
  Unsubscribe,
  VIPDashboard,
  VIPProfilePage,
  VIPSettingsPage,
} from '@/routes/lazyRoutes';
import CookieConsentBanner from './components/shared/CookieConsentBanner';
import Auth from './pages/Auth';
// Eager imports — critical path for landing + auth
import Launch from './pages/Launch';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: true,
    },
  },
  mutationCache: new MutationCache({
    onError: (error) => {
      toast.error(error.message || 'Something went wrong');
    },
  }),
});

const App = () => (
  <Sentry.ErrorBoundary
    fallback={
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <div className="max-w-md text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-amber-400/10">
            <AlertTriangle className="h-8 w-8 text-amber-400" />
          </div>
          <p className="font-display text-lg font-semibold text-foreground">VenueIntel</p>
          <h2 className="mt-4 text-base font-medium text-foreground">Something went wrong</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            An unexpected error occurred. Please refresh the page.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="mt-6 inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
          >
            Refresh Page
          </button>
        </div>
      </div>
    }
  >
    <QueryClientProvider client={queryClient}>
      <DemoModeProvider>
        <VenueProfileProvider>
          <EntitlementsProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <QueryErrorBoundary>
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <Routes>
                      {/* Public routes — eager */}
                      <Route path="/" element={<Launch />} />
                      <Route path="/auth" element={<Auth />} />

                      {/* Public routes — lazy */}
                      <Route path="/original-home" element={<Index />} />
                      <Route path="/design-partners" element={<DesignPartners />} />
                      <Route path="/our-story" element={<OurStory />} />
                      <Route
                        path="/blog/hidden-cost-disconnected-venue-technology"
                        element={<HiddenCostArticle />}
                      />
                      <Route path="/unsubscribe" element={<Unsubscribe />} />
                      <Route path="/privacy" element={<PrivacyPolicy />} />
                      <Route path="/terms" element={<TermsOfService />} />
                      <Route path="/security" element={<SecurityPolicy />} />
                      <Route path="/sub-processors" element={<SubProcessors />} />
                      {/* <Route path="/pricing" element={<Pricing />} /> */}
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/reset-password" element={<ResetPassword />} />
                      <Route path="/invite" element={<Invite />} />

                      {/* Protected routes - require authentication */}
                      <Route
                        path="/onboarding"
                        element={
                          <AuthGuard>
                            <LaunchGate>
                              <Onboarding />
                            </LaunchGate>
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/dashboard"
                        element={
                          <AuthGuard>
                            <LaunchGate>
                              <Dashboard />
                            </LaunchGate>
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/getting-started"
                        element={
                          <AuthGuard>
                            <LaunchGate>
                              <GettingStarted />
                            </LaunchGate>
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/events"
                        element={
                          <AuthGuard>
                            <LaunchGate>
                              <Events />
                            </LaunchGate>
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/events/:id/gm-report"
                        element={
                          <AuthGuard>
                            <LaunchGate>
                              <RoleGuard requireGMAccess>
                                <GMReportPage />
                              </RoleGuard>
                            </LaunchGate>
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/calendar"
                        element={
                          <AuthGuard>
                            <LaunchGate>
                              <Calendar />
                            </LaunchGate>
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/reports"
                        element={
                          <AuthGuard>
                            <LaunchGate>
                              <ReportsPage />
                            </LaunchGate>
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/hyped"
                        element={
                          <AuthGuard>
                            <LaunchGate>
                              <Hyped />
                            </LaunchGate>
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/hyped/:tab"
                        element={
                          <AuthGuard>
                            <LaunchGate>
                              <Hyped />
                            </LaunchGate>
                          </AuthGuard>
                        }
                      />

                      {/* Legacy redirect */}
                      <Route
                        path="/watchlist"
                        element={<Navigate to="/hyped/watchlist" replace />}
                      />

                      {/* Marketing Hub routes - protected */}
                      <Route
                        path="/marketing"
                        element={<Navigate to="/marketing/overview" replace />}
                      />
                      <Route
                        path="/marketing/overview"
                        element={
                          <AuthGuard>
                            <LaunchGate>
                              <MarketingOverview />
                            </LaunchGate>
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/marketing/email"
                        element={
                          <AuthGuard>
                            <LaunchGate>
                              <MarketingEmail />
                            </LaunchGate>
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/marketing/sms"
                        element={
                          <AuthGuard>
                            <LaunchGate>
                              <MarketingSmsPage />
                            </LaunchGate>
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/marketing/audiences"
                        element={
                          <AuthGuard>
                            <LaunchGate>
                              <MarketingAudiencesPage />
                            </LaunchGate>
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/marketing/meta-ads"
                        element={
                          <AuthGuard>
                            <LaunchGate>
                              <MarketingMetaAds />
                            </LaunchGate>
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/marketing/instagram"
                        element={
                          <AuthGuard>
                            <LaunchGate>
                              <MarketingInstagramGrowth />
                            </LaunchGate>
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/marketing/settings"
                        element={
                          <AuthGuard>
                            <LaunchGate>
                              <MarketingSettingsPage />
                            </LaunchGate>
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/marketing/distribution"
                        element={
                          <AuthGuard>
                            <LaunchGate>
                              <MarketingDistribution />
                            </LaunchGate>
                          </AuthGuard>
                        }
                      />

                      {/* Legacy redirects */}
                      <Route
                        path="/marketing/social"
                        element={<Navigate to="/analytics/social" replace />}
                      />
                      <Route
                        path="/marketing/promotions"
                        element={<Navigate to="/marketing/overview" replace />}
                      />
                      <Route
                        path="/syndication"
                        element={<Navigate to="/marketing/overview" replace />}
                      />
                      <Route
                        path="/syndication/*"
                        element={<Navigate to="/marketing/overview" replace />}
                      />

                      {/* Analytics Hub routes - protected */}
                      <Route
                        path="/analytics"
                        element={
                          <AuthGuard>
                            <LaunchGate>
                              <AnalyticsLayout />
                            </LaunchGate>
                          </AuthGuard>
                        }
                      >
                        <Route index element={<Navigate to="/analytics/events" replace />} />
                        <Route path="events" element={<EventOverlayDashboard />} />
                        <Route path="meta" element={<MetaAdsDashboard />} />
                        <Route path="google" element={<GoogleAdsDashboard />} />
                        <Route path="social" element={<SocialPerformanceDashboard />} />
                      </Route>

                      {/* Insights Hub routes - protected */}
                      <Route
                        path="/insights"
                        element={
                          <AuthGuard>
                            <LaunchGate>
                              <Insights />
                            </LaunchGate>
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/insights/:tab"
                        element={
                          <AuthGuard>
                            <LaunchGate>
                              <Insights />
                            </LaunchGate>
                          </AuthGuard>
                        }
                      />

                      {/* Legacy redirects */}
                      <Route
                        path="/event-radar"
                        element={<Navigate to="/insights/event-radar" replace />}
                      />
                      <Route
                        path="/venue-comparison"
                        element={<Navigate to="/insights/venue-comparison" replace />}
                      />

                      {/* Settings & Account - protected with role requirements */}
                      <Route
                        path="/settings"
                        element={
                          <AuthGuard>
                            <LaunchGate>
                              <Settings />
                            </LaunchGate>
                          </AuthGuard>
                        }
                      />

                      {/* Account Settings with nested routes - protected */}
                      <Route
                        path="/account"
                        element={
                          <AuthGuard>
                            <LaunchGate>
                              <AccountLayout />
                            </LaunchGate>
                          </AuthGuard>
                        }
                      >
                        <Route index element={<Navigate to="/account/contact-info" replace />} />
                        <Route path="contact-info" element={<ContactInfo />} />
                        <Route path="change-email" element={<ChangeEmail />} />
                        <Route path="password" element={<Password />} />
                        <Route path="payment-methods" element={<PaymentMethods />} />
                        <Route path="linked-accounts" element={<LinkedAccounts />} />
                        <Route
                          path="communication-preferences"
                          element={<CommunicationPreferences />}
                        />
                        <Route path="close-account" element={<CloseAccount />} />
                        <Route path="personal-data" element={<PersonalData />} />
                      </Route>

                      {/* Team management - protected with admin requirement */}
                      <Route
                        path="/team"
                        element={
                          <AuthGuard>
                            <LaunchGate>
                              <RoleGuard requireAdmin>
                                <Team />
                              </RoleGuard>
                            </LaunchGate>
                          </AuthGuard>
                        }
                      />

                      {/* CRM / VIP Intelligence routes - protected */}
                      <Route path="/crm" element={<Navigate to="/crm/vip" replace />} />
                      <Route
                        path="/crm/vip"
                        element={
                          <AuthGuard>
                            <LaunchGate>
                              <RoleGuard>
                                <VIPDashboard />
                              </RoleGuard>
                            </LaunchGate>
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/crm/vip/settings"
                        element={
                          <AuthGuard>
                            <LaunchGate>
                              <RoleGuard>
                                <VIPSettingsPage />
                              </RoleGuard>
                            </LaunchGate>
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/crm/vip/:id"
                        element={
                          <AuthGuard>
                            <LaunchGate>
                              <RoleGuard>
                                <VIPProfilePage />
                              </RoleGuard>
                            </LaunchGate>
                          </AuthGuard>
                        }
                      />

                      {/* Demo data - available to all authenticated users */}
                      <Route
                        path="/admin/demo-data"
                        element={
                          <AuthGuard>
                            <DemoDataAdmin />
                          </AuthGuard>
                        }
                      />

                      {/* Super Admin routes */}
                      <Route
                        path="/admin"
                        element={
                          <AuthGuard>
                            <SuperAdminGuard>
                              <AdminLayout />
                            </SuperAdminGuard>
                          </AuthGuard>
                        }
                      >
                        <Route index element={<AdminDashboard />} />
                        <Route path="tables" element={<AdminTableBrowser />} />
                        <Route path="tables/:tableName" element={<AdminTableBrowser />} />
                        <Route path="entitlements" element={<AdminEntitlements />} />
                        <Route path="audit-log" element={<AdminAuditLog />} />
                      </Route>

                      {/* Pitch deck screenshot pages - no auth */}
                      <Route path="/pitch" element={<PitchScreenshots />} />
                      <Route path="/pitch/dashboard" element={<PitchDashboard />} />
                      <Route path="/pitch/gm-report" element={<PitchGMReport />} />
                      <Route path="/pitch/vip-profile" element={<PitchVIPProfile />} />
                      <Route path="/pitch/marketing" element={<PitchMarketing />} />
                      <Route path="/pitch/integrations" element={<PitchIntegrations />} />
                      <Route path="/pitch/staffing" element={<PitchStaffing />} />
                      <Route path="/pitch/hyped" element={<PitchHyped />} />
                      <Route path="/pitch/insights" element={<PitchInsights />} />

                      {/* 404 */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </QueryErrorBoundary>
                <CookieConsentBanner />
              </BrowserRouter>
            </TooltipProvider>
          </EntitlementsProvider>
        </VenueProfileProvider>
      </DemoModeProvider>
    </QueryClientProvider>
  </Sentry.ErrorBoundary>
);

export default App;
